import React, { useState } from 'react';
import { Button, Tooltip } from '@mui/material';
import { FileDownload as ExportIcon } from '@mui/icons-material';
import { ExportModal } from './ExportModal';
import { ExportService } from '../services/ExportService';
import { AnalyticsDataPoint, TableColumn } from '../types/analytics.types';

interface ExportButtonProps {
  data: AnalyticsDataPoint[];
  columns: TableColumn[];
  filename?: string;
  disabled?: boolean;
  highlightDeviations?: boolean;
  calculateDeviation?: (row: any, column: any, value: any) => any;
  varianceDisplayMode?: 'absolute' | 'percent';
}

export const ExportButton: React.FC<ExportButtonProps> = ({
  data,
  columns,
  filename = 'analytics_data',
  disabled = false,
  highlightDeviations = false,
  calculateDeviation,
  varianceDisplayMode = 'percent'
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleExport = async (format: 'csv' | 'xlsx' | 'pdf' | 'email') => {
    setLoading(true);
    
    try {
      // Pass variance props to export service
      const exportOptions = {
        highlightDeviations,
        calculateDeviation,
        varianceDisplayMode
      };

      switch (format) {
        case 'csv':
          ExportService.exportToCSV(data, columns, filename, exportOptions);
          break;
        case 'xlsx':
          ExportService.exportToXLSX(data, columns, filename, exportOptions);
          break;
        case 'pdf':
          ExportService.exportToPDF(data, columns, filename, exportOptions);
          break;
        case 'email':
          ExportService.shareViaEmail(data, columns, filename, exportOptions);
          break;
      }
      
      setModalOpen(false);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportInfo = ExportService.getExportData(data, columns);

  return (
    <>
      <Tooltip 
        title={`Export ${exportInfo.recordCount} records (${exportInfo.size})`}
        placement="top"
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<ExportIcon />}
          onClick={() => setModalOpen(true)}
          disabled={disabled || data.length === 0}
          sx={{
            fontFamily: "'Inter', sans-serif",
            fontSize: '14px',
            fontWeight: 400,
            color: '#18181b',
            borderColor: '#e4e4e7',
            borderRadius: '6px',
            textTransform: 'none',
            padding: '8px 16px',
            '&:hover': {
              borderColor: '#18181b',
              backgroundColor: '#fafafa',
            },
            '&.Mui-disabled': {
              color: '#71717a',
              borderColor: '#e4e4e7',
            },
          }}
        >
          Export
        </Button>
      </Tooltip>

      <ExportModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onExport={handleExport}
        loading={loading}
      />
    </>
  );
};